import { suggestBehaviorFromPeer } from './peerCalibrationLink';
import { taskCategoryLabel, evalGroupLabel } from './taskProcess';

export type HrMonitorMember = {
  id: string;
  name?: string;
  duty?: string;
  department?: string;
  evalGroup?: string;
  finalPerf?: string;
  finalBehavior?: string;
  isAdmin?: boolean;
};

export type HrMonitorTask = {
  id: string;
  ownerId?: string;
  status?: string;
  taskCategory?: string;
  quarterReviews?: Record<string, { confirmed?: boolean; score?: number }>;
};

export type HrMonitorPool = {
  subjectId?: string;
  confirmed?: boolean;
  selectedIds?: string[];
};

export type HrMonitorSummary = {
  colleagueId?: string;
  participantCount?: number;
  byCategory?: unknown;
};

export type HrAlert = {
  level: 'danger' | 'warn' | 'info';
  title: string;
  detail: string;
};

export type HrMonitorSnapshot = {
  evalYear: number;
  memberCount: number;
  employeeCount: number;
  assign: {
    registered: number;
    approved: number;
    rejected: number;
    totalTasks: number;
    membersWithoutTask: number;
    byCategory: Record<string, number>;
  };
  quarters: {
    confirmedCells: number;
    totalCells: number;
    confirmRate: number;
  };
  peer: {
    poolsConfirmed: number;
    poolsTotal: number;
    membersWithReport: number;
    avgParticipants: number;
    divergeFromSuggest: number;
  };
  grades: {
    perf: Record<string, number>;
    behavior: Record<string, number>;
    exRate: number;
    isLenient: boolean;
  };
  byDept: Array<{
    department: string;
    members: number;
    approvedTasks: number;
    poolConfirmed: number;
    peerReports: number;
    exCount: number;
    exRate: number;
  }>;
  byEvalGroup: Array<{ group: string; members: number; exCount: number; exRate: number }>;
  alerts: HrAlert[];
};

function countGrade(map: Record<string, number>, grade?: string) {
  const g = grade || 'GD';
  map[g] = (map[g] || 0) + 1;
}

/**
 * 선택 연도 기준 HR 모니터링 스냅샷 생성
 */
export function buildHrMonitorSnapshot(input: {
  evalYear: number;
  members: HrMonitorMember[];
  tasks: HrMonitorTask[];
  pools: Record<string, HrMonitorPool>;
  peerReports: Record<string, HrMonitorSummary>;
  gradesFor: (m: HrMonitorMember) => { finalPerf: string; finalBehavior: string };
}): HrMonitorSnapshot {
  const { evalYear, members, tasks, pools, peerReports, gradesFor } = input;
  const employees = members.filter((m) => !m.isAdmin && m.duty !== '담당자');
  const employeeIds = new Set(employees.map((m) => m.id));

  const assign = {
    registered: 0,
    approved: 0,
    rejected: 0,
    totalTasks: tasks.length,
    membersWithoutTask: 0,
    byCategory: {} as Record<string, number>,
  };
  const ownersWithTask = new Set<string>();
  tasks.forEach((t) => {
    if (t.status === 'APPROVED') assign.approved += 1;
    else if (t.status === 'REJECTED') assign.rejected += 1;
    else assign.registered += 1;
    if (t.ownerId) ownersWithTask.add(t.ownerId);
    const cat = taskCategoryLabel(t.taskCategory);
    assign.byCategory[cat] = (assign.byCategory[cat] || 0) + 1;
  });
  assign.membersWithoutTask = employees.filter((m) => !ownersWithTask.has(m.id)).length;

  let confirmedCells = 0;
  let totalCells = 0;
  tasks
    .filter((t) => t.status === 'APPROVED')
    .forEach((t) => {
      ['Q1', 'Q2', 'Q3', 'Q4'].forEach((q) => {
        totalCells += 1;
        if (t.quarterReviews?.[q]?.confirmed) confirmedCells += 1;
      });
    });
  const confirmRate = totalCells ? Math.round((confirmedCells / totalCells) * 100) : 0;

  const poolList = Object.values(pools);
  const poolsConfirmed = poolList.filter((p) => p.confirmed).length;
  const reportEntries = Object.values(peerReports);
  const membersWithReport = reportEntries.filter((r) => Number(r.participantCount || 0) > 0).length;
  const avgParticipants = reportEntries.length
    ? Math.round(
        (reportEntries.reduce((s, r) => s + Number(r.participantCount || 0), 0) / reportEntries.length) * 10
      ) / 10
    : 0;

  let divergeFromSuggest = 0;
  const perf: Record<string, number> = { EX: 0, GD: 0, NI: 0 };
  const behavior: Record<string, number> = { EX: 0, GD: 0, NI: 0 };

  employees.forEach((m) => {
    const g = gradesFor(m);
    countGrade(perf, g.finalPerf);
    countGrade(behavior, g.finalBehavior);
    const suggest = suggestBehaviorFromPeer(peerReports[m.id] as never);
    if (suggest && suggest.grade !== g.finalBehavior) divergeFromSuggest += 1;
  });

  const memberCountForEx = employees.length || 1;
  const exRate = Math.round(((perf.EX || 0) / memberCountForEx) * 100);
  const isLenient = exRate > 20;

  const deptMap = new Map<
    string,
    { members: number; approvedTasks: number; poolConfirmed: number; peerReports: number; exCount: number }
  >();
  employees.forEach((m) => {
    const dept = m.department || '미지정';
    const cur = deptMap.get(dept) || {
      members: 0,
      approvedTasks: 0,
      poolConfirmed: 0,
      peerReports: 0,
      exCount: 0,
    };
    cur.members += 1;
    if (pools[m.id]?.confirmed) cur.poolConfirmed += 1;
    if (Number(peerReports[m.id]?.participantCount || 0) > 0) cur.peerReports += 1;
    if (gradesFor(m).finalPerf === 'EX') cur.exCount += 1;
    deptMap.set(dept, cur);
  });
  tasks.forEach((t) => {
    if (t.status !== 'APPROVED' || !t.ownerId) return;
    const owner = members.find((m) => m.id === t.ownerId);
    if (!owner || !employeeIds.has(owner.id)) return;
    const dept = owner.department || '미지정';
    const cur = deptMap.get(dept);
    if (!cur) return;
    cur.approvedTasks += 1;
  });

  const byDept = Array.from(deptMap.entries())
    .map(([department, v]) => ({
      department,
      ...v,
      exRate: v.members ? Math.round((v.exCount / v.members) * 100) : 0,
    }))
    .sort((a, b) => a.department.localeCompare(b.department, 'ko'));

  const groupMap = new Map<string, { members: number; exCount: number }>();
  employees.forEach((m) => {
    const group = evalGroupLabel(m.evalGroup);
    const cur = groupMap.get(group) || { members: 0, exCount: 0 };
    cur.members += 1;
    if (gradesFor(m).finalPerf === 'EX') cur.exCount += 1;
    groupMap.set(group, cur);
  });
  const byEvalGroup = Array.from(groupMap.entries()).map(([group, v]) => ({
    group,
    members: v.members,
    exCount: v.exCount,
    exRate: v.members ? Math.round((v.exCount / v.members) * 100) : 0,
  }));

  const alerts: HrAlert[] = [];
  if (isLenient) {
    alerts.push({
      level: 'danger',
      title: 'EX 관대화 경고',
      detail: `성과 EX 비율 ${exRate}% (기준 20%). Calibration에서 근거를 재검증하세요.`,
    });
  }
  if (assign.membersWithoutTask > 0) {
    alerts.push({
      level: 'warn',
      title: '과제 Assign 미등록',
      detail: `팀원 ${assign.membersWithoutTask}명에게 ${evalYear}년 중점과제가 없습니다.`,
    });
  }
  if (assign.registered > 0) {
    alerts.push({
      level: 'warn',
      title: 'Assign 승인 대기',
      detail: `승인 대기 과제 ${assign.registered}건이 남아 있습니다.`,
    });
  }
  if (totalCells > 0 && confirmRate < 50) {
    alerts.push({
      level: 'warn',
      title: '분기 Confirm 저조',
      detail: `승인 과제의 분기 Confirm 비율 ${confirmRate}% (${confirmedCells}/${totalCells}).`,
    });
  }
  if (employees.length > 0 && poolsConfirmed < employees.length) {
    alerts.push({
      level: 'info',
      title: '다면 평가자 풀 미확정',
      detail: `풀 확정 ${poolsConfirmed}/${employees.length}명. 미확정 인원은 설문 노출이 제한될 수 있습니다.`,
    });
  }
  if (divergeFromSuggest > 0) {
    alerts.push({
      level: 'info',
      title: '다면 추천과 방식 등급 불일치',
      detail: `${divergeFromSuggest}명의 일하는 방식 등급이 다면평가 추천과 다릅니다. Calibration에서 확인하세요.`,
    });
  }
  if (alerts.length === 0) {
    alerts.push({
      level: 'info',
      title: '특이 경보 없음',
      detail: `${evalYear}년 모니터링 지표가 기준 범위 안입니다.`,
    });
  }

  return {
    evalYear,
    memberCount: members.length,
    employeeCount: employees.length,
    assign,
    quarters: { confirmedCells, totalCells, confirmRate },
    peer: {
      poolsConfirmed,
      poolsTotal: employees.length,
      membersWithReport,
      avgParticipants,
      divergeFromSuggest,
    },
    grades: { perf, behavior, exRate, isLenient },
    byDept,
    byEvalGroup,
    alerts,
  };
}
